import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  isPasswordValid,
  type PasswordAvoidReason,
  type PasswordCheck,
  type PasswordRuleId,
} from './policy/passwordPolicy';
import type { PwnedStatus } from './hooks/usePwnedStatus';

export const INCLUDE_RULES = PASSWORD_RULES.filter((rule) => rule.kind === 'include');
const AVOID_RULES = PASSWORD_RULES.filter((rule) => rule.kind === 'avoid');
const INCLUDE_IDS = new Set<PasswordRuleId>(INCLUDE_RULES.map((rule) => rule.id));

export type StrengthLevel = 0 | 1 | 2 | 3 | 4;

/** Steps of the strength bar: the top level, «Strong». */
export const MAX_STRENGTH_LEVEL = 4;

/** With this many of the five composition rules met, the bar reads «Fair». */
const FAIR_MIN_RULES_MET = 3;

/** What broke the password: length, a rule to avoid, the breach lookup, or the API. */
export type PasswordIssue =
  | { kind: 'length' }
  | { kind: 'reason'; reason: PasswordAvoidReason | 'pwned' }
  | { kind: 'server'; rule: PasswordRuleId | null };

export interface PasswordStatus {
  empty: boolean;
  locallyValid: boolean;
  issue?: PasswordIssue;
  /** Every local rule is met and the breach lookup is still running. */
  checking: boolean;
  /** Good to submit: every rule met, not breached (or the lookup unavailable). */
  done: boolean;
  level: StrengthLevel;
}

/** Rule the API named when it rejected this very password; `undefined` if it did not. */
type RejectedRule = PasswordRuleId | null | undefined;

/** What breaks the password, in the order the screen explains it: the length
 *  ceiling, a rule to avoid, the breach lookup, then the API's own rejection. */
function findIssue(
  password: string,
  check: PasswordCheck,
  pwned: PwnedStatus,
  rejectedRule: RejectedRule,
): PasswordIssue | undefined {
  if (
    password.length > 2 * PASSWORD_MAX_LENGTH ||
    Array.from(password).length > PASSWORD_MAX_LENGTH
  ) {
    return { kind: 'length' };
  }
  const broken = AVOID_RULES.find((rule) => !check[rule.id].ok);
  if (broken) return { kind: 'reason', reason: check[broken.id].reason ?? 'common' };
  if (pwned === 'pwned') return { kind: 'reason', reason: 'pwned' };
  if (rejectedRule !== undefined) return { kind: 'server', rule: rejectedRule };
  return undefined;
}

/** The strength bar: nothing typed, broken, done, or by how many of the
 *  composition rules are met so far. */
function strengthLevel({
  empty,
  issue,
  done,
  met,
}: {
  empty: boolean;
  issue?: PasswordIssue;
  done: boolean;
  met: number;
}): StrengthLevel {
  if (empty) return 0;
  if (issue) return 1;
  if (done) return MAX_STRENGTH_LEVEL;
  if (met === INCLUDE_RULES.length) return 3;
  return met >= FAIR_MIN_RULES_MET ? 2 : 1;
}

/**
 * What the checklist shows for a password, decided once so the panel and the
 * screen-reader announcement never disagree. Mirrors the Design prototype:
 * what is missing is not an issue while typing; what breaks a rule to avoid
 * is, from the first character; a rejection from the API counts as one only
 * for the exact password it was about.
 */
export function describePassword({
  password,
  check,
  pwned,
  rejectedRule,
}: {
  password: string;
  check: PasswordCheck;
  pwned: PwnedStatus;
  rejectedRule?: RejectedRule;
}): PasswordStatus {
  const empty = password.length === 0;
  const locallyValid = isPasswordValid(check);
  const met = INCLUDE_RULES.filter((rule) => check[rule.id].ok).length;
  const issue = empty ? undefined : findIssue(password, check, pwned, rejectedRule);
  const lookupSettled = pwned === 'clean' || pwned === 'unavailable';
  const done = locallyValid && !issue && lookupSettled;
  const checking = locallyValid && !issue && pwned === 'checking';

  return {
    empty,
    locallyValid,
    issue,
    checking,
    done,
    level: strengthLevel({ empty, issue, done, met }),
  };
}

/** The translation key (and values) that explains an issue. */
export function issueMessage(issue: PasswordIssue): {
  key: string;
  values?: Record<string, unknown>;
} {
  if (issue.kind === 'reason') return { key: 'server.policy' };
  if (issue.kind === 'length' || issue.rule === 'length') {
    return {
      key: 'server.length',
      values: { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH },
    };
  }
  // Composition rules name the missing requirement; all other rejections use
  // the policy's generic message, as the ticket requires for the form.
  return issue.rule && INCLUDE_IDS.has(issue.rule)
    ? { key: `server.${issue.rule}`, values: { min: PASSWORD_MIN_LENGTH } }
    : { key: 'server.policy' };
}
